// Dependencies
import React, { useState, useEffect, useCallback, useMemo } from "react";
import ePub from "epubjs";
import { useIntl } from "react-intl";
import { Box, Paper, Typography } from "@mui/material";
import { Label, Marker } from "./TImeManagmentStats";
import { makeStyles } from "@mui/styles";
import PieChart from "./PieChart";
// Import PDF CFI handling
import PdfCFI from "../../../utils/pdf-cfi";
// Import selector to check if document is PDF
import { isPdfSelector } from "../../../redux/textsSlice";
import { useSelector } from "react-redux";

// Styles
const useStyles = makeStyles(() => ({
  textTitle: {
    flexBasis: "100%",
    marginBottom: "16px",
    fontWeight: "500"
  },
  legend: {
    display: "flex",
    flexDirection: "column"
  },
  item: {
    width: "100%",
    paddingBlock: "16px", // Reduced padding for better visual fit
    display: "flex",
    flexDirection: "row",
    alignItems: "center"
  },
  chart: {
    padding: "0",
    minHeight: "300px",
    height: "100%"
  },
  label: {
    fontSize: "16px !important",
    display: "inline-block",
    textAlign: "left",
    verticalAlign: "middle",
    "& span": {
      fontSize: "16px !important"
    }
  },
  legendContainer: {
    flex: "0 0 35%" // Increased width for better text display
  }
}));

export default function CitationChart({
  submissions = [],
  question = {},
  answers = []
}) {
  // Hooks
  const classes = useStyles();

  // Check if document is PDF
  const isPdf = useSelector(isPdfSelector);

  // Ephemeral State
  const [stats, setStats] = useState([]);
  const intl = useIntl();

  // Initialize EpubCFI once
  const EpubCFI = useMemo(() => new ePub.CFI(), []);

  // Derived state
  const subCompare = (needle, haystack, min_substring_length = 1) => {
    // Search possible substrings from largest to smallest:
    for (var i = needle.length; i >= min_substring_length; i--) {
      for (var j = 0; j <= needle.length - i; j++) {
        var substring = needle.substr(j, i);
        var k = haystack.indexOf(substring);
        if (k != -1) {
          return {
            found: 1,
            substring: substring,
            needleIndex: j,
            haystackIndex: k
          };
        }
      }
    }
    return {
      found: 0
    };
  };
  // Initialize PDF CFI instance once
  const pdfCFIInstance = useMemo(() => {
    try {
      return new PdfCFI();
    } catch (error) {
      console.error("Failed to initialize PdfCFI:", error);
      return null;
    }
  }, []);

  const handleCompareCFI = useCallback(
    (a, b) => {
      try {
        if (isPdf) {
          // Safe comparison for PDF - if PdfCFI fails, return 0
          try {
            if (pdfCFIInstance) {
              return pdfCFIInstance.compare(a, b);
            }
            console.warn("PDF CFI instance not available");
            return 0;
          } catch (error) {
            console.warn("PDF CFI comparison failed:", error);
            return 0;
          }
        } else {
          // EPUB comparison
          return EpubCFI.compare(a.cfi, b.cfi);
        }
      } catch (error) {
        console.error("CFI comparison failed:", error);
        return 0;
      }
    },
    [EpubCFI, isPdf, pdfCFIInstance]
  );

  const calcScore = useCallback(
    (question, answer) => {
      try {
        if (!question || !answer) return 0;

        // The teacher's answer is saved in the interaction metadata as quotes:[{text: String, cfi: String}]
        const teacherAnswer = question.quotes;

        let tempScore = 0;

        if (!answer.length) return 0;
        if (!teacherAnswer || !teacherAnswer.length) return 1;

        // Sort the quotes based on their CFIs
        let teacherSortedQuotes;
        let studentSorted;

        try {
          teacherSortedQuotes = [...teacherAnswer].sort((a, b) =>
            handleCompareCFI(a, b)
          );

          studentSorted = [...answer].sort((a, b) => handleCompareCFI(a, b));
        } catch (error) {
          console.error("Error sorting quotes:", error);
          return 0;
        }

        let studentInd = 0;
        let teacherInd = 0;

        // Compare quotes and calculate score
        while (
          studentInd < studentSorted.length &&
          teacherInd < teacherSortedQuotes.length
        ) {
          // Get the content
          const studentText =
            studentSorted[studentInd].content ||
            studentSorted[studentInd].text ||
            "";
          const teacherText =
            teacherSortedQuotes[teacherInd].content ||
            teacherSortedQuotes[teacherInd].text ||
            "";

          // Simple comparison - check if texts overlap
          let needle = studentText;
          let haystack = teacherText;

          if (haystack.length < needle.length) {
            haystack = needle;
            needle = teacherText;
          }

          let retVal = subCompare(needle, haystack);
          if (retVal.found) {
            tempScore += retVal.substring.length;
          }

          // Move to next items
          studentInd++;
          teacherInd++;
        }

        let totalTeacher = teacherSortedQuotes.reduce(
          (acc, curr) => acc + (curr.content?.length || curr.text?.length || 0),
          0
        );

        return totalTeacher > 0 ? (1.0 * tempScore) / totalTeacher : 0;
      } catch (error) {
        console.error("Error calculating score:", error);
        return 0;
      }
    },
    [handleCompareCFI]
  );

  useEffect(() => {
    const PARTIAL_THRESHOLD = 20; // Threshold for partial matches
    const SPOT_ON_THRESHOLD = 70; // Threshold for "right on the spot"

    if (submissions && submissions.length) {
      try {
        let submissionsToAnswer = {};

        // Ensure answers is an array before trying to iterate
        if (Array.isArray(answers)) {
          answers.forEach((answer) => {
            if (answer && answer.submission_id) {
              if (!(answer.submission_id in submissionsToAnswer)) {
                submissionsToAnswer[answer.submission_id] = [];
              }
              submissionsToAnswer[answer.submission_id].push(answer);
            }
          });
        }

        // Calculate score for each submission
        let matches = Object.entries(submissionsToAnswer).map(
          ([_, submissionAnswers]) => {
            try {
              return calcScore(question, submissionAnswers) * 100;
            } catch (error) {
              console.error("Error calculating individual score:", error);
              return 0;
            }
          }
        );

        // Calculate how many submissions fall into each category
        const rightOnSpot = matches.filter((a) => a > SPOT_ON_THRESHOLD).length;
        const partialMatch = matches.filter(
          (a) => a > PARTIAL_THRESHOLD && a <= SPOT_ON_THRESHOLD
        ).length;
        const divergent = matches.filter((a) => a <= PARTIAL_THRESHOLD).length;

        // Calculate percentages
        const totalSubmissions = matches.length || 1; // Avoid division by zero
        const rightOnSpotPercent = Math.round(
          (rightOnSpot / totalSubmissions) * 100
        );
        const partialMatchPercent = Math.round(
          (partialMatch / totalSubmissions) * 100
        );
        const divergentPercent = Math.round(
          (divergent / totalSubmissions) * 100
        );

        setStats([
          {
            cat: intl.formatMessage({
              id: "task.statistics.rightOnTheSpot",
              defaultMessage: "Right on the spot"
            }),
            val: rightOnSpot,
            percent: rightOnSpotPercent,
            color: "#168fee"
          },
          {
            cat: intl.formatMessage({
              id: "task.statistics.prettyClose",
              defaultMessage: "Partial match"
            }),
            val: partialMatch,
            percent: partialMatchPercent,
            color: "#BCD9F1"
          },
          {
            cat: intl.formatMessage({
              id: "task.statistics.wayOff",
              defaultMessage: "Divergent selections"
            }),
            val: divergent,
            percent: divergentPercent,
            color: "#FF6432"
          }
        ]);
      } catch (error) {
        console.error("Error processing submissions:", error);
        setStatsForError();
      }
    } else {
      setStatsForError();
    }

    function setStatsForError() {
      setStats([
        {
          cat: intl.formatMessage({
            id: "task.statistics.noSubmissionsYet",
            defaultMessage: "No submissions yet"
          }),
          val: 1,
          percent: 100,
          color: "#D8D8D8"
        }
      ]);
    }
  }, [intl, answers, calcScore, question, submissions]);

  return (
    <Paper elevation={0}>
      {/* Render the title */}
      <Box component="header">
        <Typography variant="subtitle1" className={classes.textTitle}>
          {intl.formatMessage({
            id: "task.statistics.performance",
            defaultMessage: "Student performance"
          })}
        </Typography>
      </Box>
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        {/* Render the legend with percentages */}
        <Box className={classes.legendContainer}>
          <Box className={classes.legend}>
            {stats
              .filter((s) => s.val > 0)
              .map((dataPoint, index) => (
                <Box key={index} className={classes.item}>
                  <Marker
                    style={{ marginBottom: "0", marginRight: "8px" }}
                    color={dataPoint.color}
                  />
                  <Label>
                    {dataPoint.cat} ({dataPoint.percent}%)
                  </Label>
                </Box>
              ))}
          </Box>
        </Box>
        {/* Render the chart using D3.js */}
        <PieChart data={stats} width="200px" height={250} />
      </Box>
    </Paper>
  );
}
